import React from 'react'
import Chip from '@mui/material/Chip'
import type { SxProps, Theme } from '@mui/material/styles'
import { useTheme } from '@mui/material/styles'
import {
  CHIP_FONT,
  CHIP_HEIGHT,
  CHIP_HEIGHT_DENSE,
  CHIP_MAX_WIDTH,
  type StatusTone,
} from 'core/theme.tokens'

interface StatusChipProps {
  tone: StatusTone
  label: string
  size?: 'small' | 'medium'
  /** Tighter 20px height for chip rows inside dense cards. */
  dense?: boolean
  /** Cap width and ellipsize; defaults to the shared 160px cap. */
  maxWidth?: number | false
  icon?: React.ReactElement
  onDelete?: () => void
  sx?: SxProps<Theme>
}

export const StatusChip: React.FC<StatusChipProps> = ({
  tone,
  label,
  size = 'small',
  dense = false,
  maxWidth = CHIP_MAX_WIDTH,
  icon,
  onDelete,
  sx,
}) => {
  const theme = useTheme()
  const pair = theme.custom.status[tone]

  return (
    <Chip
      size={size}
      label={label}
      icon={icon}
      onDelete={onDelete}
      sx={{
        bgcolor: pair.bg,
        color: pair.fg,
        fontWeight: 600,
        fontSize: CHIP_FONT,
        height: dense ? CHIP_HEIGHT_DENSE : CHIP_HEIGHT,
        border: 'none',
        ...(maxWidth === false ? {} : { maxWidth }),
        '& .MuiChip-label': {
          px: 0.75,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '& .MuiChip-icon': { fontSize: '0.9rem', ml: 0.5, color: 'inherit' },
        ...sx,
      }}
    />
  )
}

export const applicationStatusTone = (status: string): StatusTone => {
  switch (status) {
    case 'signing':
      return 'info'
    case 'Ready':
      return 'info'
    case 'Corrections Needed':
      return 'danger'
    case 'Pending':
      return 'waiting'
    case 'Viewed':
      return 'purple'
    case 'Approved':
      return 'success'
    case 'Denied':
      return 'danger'
    case 'Completed':
      return 'neutral'
    default:
      return 'neutral'
  }
}

export const leadStatusTone = (status: string): StatusTone => {
  switch (status) {
    case 'new':
      return 'new'
    case 'inProgress':
      return 'inProgress'
    case 'waitingFinance':
      return 'waiting'
    case 'closed':
      return 'success'
    case 'lost':
      return 'danger'
    default:
      return 'neutral'
  }
}

export const inventoryLeadStatusTone = (status: string): StatusTone => {
  switch (status) {
    case 'pendingList':
      return 'info'
    case 'fleetConfirmation':
      return 'new'
    case 'priceNegotiation':
      return 'warning'
    case 'availableNotPurchased':
      return 'waiting'
    case 'purchasing':
      return 'purple'
    case 'won':
      return 'success'
    case 'lost':
      return 'danger'
    default:
      return 'neutral'
  }
}
